import DOMPurify from 'dompurify';
import { Link, InputLabel, OutlinedInput, Snackbar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'lib/axios';
import fileDownload from 'js-file-download';
/** Turn escaped/CRLF newlines into real `\n` so UI can render line breaks. */
export const normalizePlainText = (text) => {
    if (!text || typeof text !== 'string') {
        return text;
    }
    return text
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\n')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');
};

export const urlify = (text) => {
    if (!text) {
        return text;
    }
    const normalized = normalizePlainText(text);
    var urlRegex = /(https?:\/\/[^\s]+)/g;
    return normalized.replace(urlRegex, function (url) {
        return '<a target="_target" href="' + url + '">' + url + '</a>';
    });
};
export const textToHtml = (text) => {
    return urlify(text);
};

export const htmlContent = (content, showMore = false, count, preRef, showLess = false) => {
    const sanitizedHtml = DOMPurify.sanitize(
        `${showMore ? textToHtml(content.substr(0, count)) : textToHtml(content)} ${
            showMore
                ? '<span id="showMoreButton" class="fs-14 MuiTypography-root MuiTypography-body1 css-qcwhce-MuiTypography-root cursor-pointer" style="color: #1E7C88; font-weight: 500;">...View More</span>'
                : showLess
                ? '<span id="showLessButton" class="fs-14 MuiTypography-root MuiTypography-body1 css-qcwhce-MuiTypography-root cursor-pointer" style="color: #1E7C88; font-weight: 500;">View Less</span>'
                : ''
        }`
    );
    return (
        <pre
            ref={preRef}
            style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
            dangerouslySetInnerHTML={{
                __html: `${sanitizedHtml}`
            }}
        />
    );
};
export const processToUpper = (text) => {
    if (text) {
        return text
            .split('_')
            .map((item) => item.replace(item[0], item[0].toUpperCase()))
            .join(' ');
    } else {
        return text;
    }
};
export const timeDiffer = (now, date) => {
    var diffMs = now - date; // milliseconds between now & the given date
    var diffDays = Math.floor(diffMs / 86400000); // days
    var diffHrs = Math.floor((diffMs % 86400000) / 3600000); // hours
    var diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes

    return diffDays > 0
        ? `${diffDays}d${diffHrs > 0 ? ` ${diffHrs}h` : ''} ago`
        : diffHrs > 0
        ? `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`
        : `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
};

export const convertDate = (dateString) => {
    // Split the date string into components (year, month, day, time)
    const parts = dateString.split('T');
    const dateParts = parts[0].split('-');
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1; // Months are zero-indexed
    const day = parseInt(dateParts[2], 10);

    // Define month names for formatting
    const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];

    // Format the date string
    const formattedDate = `${day} ${monthNames[month]} ${year}`;

    return formattedDate;
};

export const calculateTimeDifference = (createdAt) => {
    const createdAtDate = new Date(createdAt);
    const now = new Date();
    const diffInMilliseconds = now - createdAtDate;

    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInYears > 0) {
        return `${diffInYears} y`;
    } else if (diffInMonths > 0) {
        return `${diffInMonths} mon`;
    } else if (diffInDays > 0) {
        return `${diffInDays} d`;
    } else if (diffInHours > 0) {
        return `${diffInHours} h`;
    } else {
        return `${diffInMinutes} min`;
    }
};

export const convertDateAndTime = (dateString, yearAndWeek = true) => {
    const dateObject = new Date(dateString);

    const monthsOfYear = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];
    // const day = daysOfWeek[dateObject.getDay()];
    const date = dateObject.getDate();
    const month = monthsOfYear[dateObject.getMonth()];
    const year = dateObject.getFullYear();
    let hours = dateObject.getHours();
    const minutes = dateObject.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    const formattedDate = yearAndWeek
        ? `${month} ${date} ${year}, ${hours}:${minutes} ${ampm}`
        : `${month} ${date} ${hours}:${minutes} ${ampm}`;

    return formattedDate;
};
export const engageInConversation = async (user_id) => {
    await axios
        .post('/messenger/start-conversation', {
            user_id: user_id.toString(),
            type: 'single'
        })
        .then(({ data }) => {
            if (data.success) {
                setActiveConversation(() => {
                    return data.chat;
                });
                setActiveUser((prev) => {
                    return data.chat.chat_users.filter((item) => {
                        if (item.user_id != JSON.parse(window.localStorage.getItem('authUser'))?.user_id) {
                            return item;
                        }
                    })[0];
                });
                setNewRoom(data.chat);
            }
        });
};

export const scrollToTopFun = (ref) => {
    if (ref.current) {
        ref.current.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }
};

export const downloadFile = async (path) => {
    try {
        const response = await axios({
            url: `${path}`,
            method: 'GET',
            responseType: 'blob'
        });
        const filename = path.split('/').pop();
        fileDownload(response.data, filename);
    } catch (err) {
        console.log('Error downloading file:', err);
    }
};

export const formatFeedbackDateTime = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = date.getHours() >= 12 ? 'pm' : 'am';

    return `${month} ${day}, ${year} | ${hours}:${minutes} ${ampm}`;
};

export const getRoleLabel = (roleType) => {
    const roleLabels = {
        admin: 'Admin',
        supervisor: 'Lead',
        manager: 'Manager',
        employee: 'User'
    };
    return roleLabels[roleType] || roleType;
};

export const getPermissionDisplayName = (permissionName) => {
    // Mapping of permission names to their display names
    const permissionNameMap = {
        // Success Story
        'Create Success Story': 'Create Success Story',
        'Edit Success Story': 'Edit Success Story',
        'Delete Success Story': 'Delete Success Story',
        'View Success Story': 'View Success Story',
        // Tasks
        'Create Tasks': 'Create Tasks',
        'Edit Tasks': 'Edit Tasks',
        'Delete Tasks': 'Delete Tasks',
        'View Tasks': 'View Tasks',
        // Projects
        'Create Projects': 'Create Projects',
        'Edit Projects': 'Edit Projects',
        'Delete Projects': 'Delete Projects',
        'View Projects': 'View Projects',
        // Goals
        'Create Goals': 'Create Goals',
        'Create Own Goals': 'Create Own Goals',
        'Edit Goals': 'Edit Goals',
        'Delete Goals': 'Delete Goals',
        'View Goals': 'View Goals',
        // Meetings
        'Create Meetings': 'Create Meetings',
        'Edit Meetings': 'Edit Meetings',
        'Delete Meetings': 'Delete Meetings',
        'View Meetings': 'View Meetings',
        // Movement & Visits
        'Create Movement & Visits': 'Create Movement & Visits',
        'Edit Movement & Visits': 'Edit Movement & Visits',
        'Delete Movement & Visits': 'Delete Movement & Visits',
        'View Movement & Visits': 'View Movement & Visits',
        // Events
        'Create Events': 'Create Events',
        'Edit Events': 'Edit Events',
        'Delete Events': 'Delete Events',
        'View Events': 'View Events',
        // Knowledge & Skill
        'Create Knowledge & Skill': 'Create Knowledge & Skill',
        'Edit Knowledge & Skill': 'Edit Knowledge & Skill',
        'Delete Knowledge & Skill': 'Delete Knowledge & Skill',
        'View Knowledge & Skill': 'View Knowledge & Skill',
        // People's Voice
        "Create People's Voice": "Create People's Voice",
        "Edit People's Voice": "Edit People's Voice",
        "Delete People's Voice": "Delete People's Voice",
        "View People's Voice": "View People's Voice",
        'Create People Voice': "Create People's Voice",
        'Edit People Voice': "Edit People's Voice",
        'Delete People Voice': "Delete People's Voice",
        'View People Voice': "View People's Voice",
        'Create voice': "Create People's Voice",
        'Edit voice': "Edit People's Voice",
        'Delete voice': "Delete People's Voice",
        'View voice': "View People's Voice",
        // Leader's Voice
        "Create Leader's Voice": "Create Leader's Voice",
        "Edit Leader's Voice": "Edit Leader's Voice",
        "Delete Leader's Voice": "Delete Leader's Voice",
        "View Leader's Voice": "View Leader's Voice",
        'Create Leaders Voice': "Create Leader's Voice",
        'Edit Leaders Voice': "Edit Leader's Voice",
        'Delete Leaders Voice': "Delete Leader's Voice",
        'View Leaders Voice': "View Leader's Voice",
        'Create leaders voice': "Create Leader's Voice",
        'Edit leaders voice': "Edit Leader's Voice",
        'Delete leaders voice': "Delete Leader's Voice",
        'View leaders voice': "View Leader's Voice",
        // News
        'Create News': 'Create News',
        'Edit News': 'Edit News',
        'Delete News': 'Delete News',
        'View News': 'View News',
        // Notice
        'Create Notice': 'Create Notice',
        'Edit Notice': 'Edit Notice',
        'Delete Notice': 'Delete Notice',
        'View Notice': 'View Notice',
        // Policy & Procedure
        'Create Policy & Procedure': 'Create Policy & Procedure',
        'Edit Policy & Procedure': 'Edit Policy & Procedure',
        'Delete Policy & Procedure': 'Delete Policy & Procedure',
        'View Policy & Procedure': 'View Policy & Procedure',
        // Recognitions
        'Create Recognitions': 'Create Recognitions',
        'Edit Recognitions': 'Edit Recognitions',
        'Delete Recognitions': 'Delete Recognitions',
        'View Recognitions': 'View Recognitions',
        // Group
        'Create Group': 'Create Group',
        'Edit Group': 'Edit Group',
        'Delete Group': 'Delete Group',
        'View Group': 'View Group',
        // User
        'Create User': 'Create User',
        'Edit User': 'Edit User',
        'Delete User': 'Delete User',
        'View User': 'View User',
        // Department
        'Create Department': 'Create Department',
        'Edit Department': 'Edit Department',
        'Delete Department': 'Delete Department',
        'View Department': 'View Department',
        // Division
        'Create Division': 'Create Division',
        'Edit Division': 'Edit Division',
        'Delete Division': 'Delete Division',
        'View Division': 'View Division'
    };

    // Try exact match first
    if (permissionNameMap[permissionName]) {
        return permissionNameMap[permissionName];
    }

    // Try case-insensitive match
    const lowerPermissionName = permissionName?.toLowerCase() || '';
    for (const [key, value] of Object.entries(permissionNameMap)) {
        if (key.toLowerCase() === lowerPermissionName) {
            return value;
        }
    }

    // Try pattern matching for common formats
    // Handle formats like "create_success_story", "create-success-story", "createSuccessStory", etc.
    const normalizedName =
        permissionName
            ?.toLowerCase()
            .replace(/[_-]/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .trim() || '';

    // Map common action patterns
    const actionMap = {
        create: 'Create',
        edit: 'Edit',
        update: 'Edit',
        delete: 'Delete',
        remove: 'Delete',
        view: 'View',
        read: 'View',
        show: 'View',
        list: 'View'
    };

    // Map resource patterns
    const resourceMap = {
        'success story': 'Success Story',
        successstory: 'Success Story',
        task: 'Task',
        tasks: 'Task',
        project: 'Project',
        projects: 'Project',
        goal: 'Goal',
        goals: 'Goal',
        meeting: 'Meetings',
        meetings: 'Meetings',
        movement: 'Movement & Visits',
        'movement & visit': 'Movement & Visits',
        'movement & visits': 'Movement & Visits',
        'movement and visit': 'Movement & Visits',
        'movement and visits': 'Movement & Visits',
        visit: 'Movement & Visits',
        visits: 'Movement & Visits',
        event: 'Events',
        events: 'Events',
        knowledge: 'Knowledge & Skill',
        'knowledge & skill': 'Knowledge & Skill',
        'knowledge and skill': 'Knowledge & Skill',
        skill: 'Knowledge & Skill',
        skills: 'Knowledge & Skill',
        'self study': 'Knowledge & Skill',
        selfstudy: 'Knowledge & Skill',
        // More specific voice resources must come before the generic "voice" key
        "leader's voice": "Leader's Voice",
        'leaders voice': "Leader's Voice",
        'leader voice': "Leader's Voice",
        "people's voice": "People's Voice",
        'peoples voice': "People's Voice",
        'people voice': "People's Voice",
        voice: "People's Voice",
        news: 'News',
        notice: 'Notice',
        policy: 'Policy & Procedure',
        'policy & procedure': 'Policy & Procedure',
        'policy and procedure': 'Policy & Procedure',
        procedure: 'Policy & Procedure',
        recognition: 'Recognitions',
        recognitions: 'Recognitions',
        reward: 'Recognitions',
        rewards: 'Recognitions',
        group: 'Group',
        groups: 'Group',
        user: 'User',
        users: 'User',
        employee: 'User',
        employees: 'User',
        department: 'Department',
        departments: 'Department',
        division: 'Division',
        divisions: 'Division'
    };

    // Prefer longest resource match so "leaders voice" beats generic "voice"
    const resourceEntries = Object.entries(resourceMap).sort((a, b) => b[0].length - a[0].length);

    // Try to extract action and resource
    for (const [actionKey, actionValue] of Object.entries(actionMap)) {
        if (normalizedName.startsWith(actionKey)) {
            const remaining = normalizedName.substring(actionKey.length).trim();
            for (const [resourceKey, resourceValue] of resourceEntries) {
                if (remaining === resourceKey || remaining.includes(resourceKey)) {
                    return `${actionValue} ${resourceValue}`;
                }
            }
        }
    }

    // If no match found, return the original name
    return permissionName;
};
